/**
 * Employee chat — mirrors api.maihrms.com `src/modules/chat/interfaces/chat.interface.ts`.
 * Enum-like unions are API values (data): never translate them.
 */

/** "channel": an app channel (Time & Attendance) — one per person, read-only notices. */
export type ChatConversationType = "direct" | "group" | "broadcast" | "channel";
export type ChatMemberRole = "owner" | "admin" | "member";
export type ChatWorkStatus = "clocked_in" | "on_break" | "clocked_out";
export type ChatListTab = "all" | "unread" | "direct" | "groups";
export type ChatMuteDuration = "off" | "1h" | "8h" | "1w" | "always";
export type ChatAttachmentKind = "image" | "video" | "audio" | "file";
/** API ChatWallpaper keys; colours live in chat-wallpapers.ts. */
export type ChatWallpaper =
  | "default"
  | "paper"
  | "mint"
  | "sky"
  | "lavender"
  | "blush"
  | "sand"
  | "aurora"
  | "sunset"
  | "ocean"
  | "dots"
  | "grid"
  | "night"
  | "night_dots";

export const CHAT_MESSAGE_TYPES = {
  TEXT: "text",
  IMAGE: "image",
  VIDEO: "video",
  FILE: "file",
  VOICE: "voice",
  GIF: "gif",
  LOCATION: "location",
  CONTACT: "contact",
  POLL: "poll",
  LINK: "link",
  SHORTCUT: "shortcut",
  SYSTEM: "system",
  /** A channel's update: metadata.notice. */
  NOTICE: "notice",
} as const;

export type ChatMessageType =
  (typeof CHAT_MESSAGE_TYPES)[keyof typeof CHAT_MESSAGE_TYPES];

export const CHAT_WORK_STATUSES: ChatWorkStatus[] = [
  "clocked_in",
  "on_break",
  "clocked_out",
];

export const CHAT_MUTE_OPTIONS: ChatMuteDuration[] = ["1h", "8h", "1w", "always"];

/** API limits (chat-message.dto.ts). */
export const CHAT_MAX_ATTACHMENTS = 10;
export const CHAT_MAX_TEXT_LENGTH = 4000;
export const CHAT_POLL_MIN_OPTIONS = 2;
export const CHAT_POLL_MAX_OPTIONS = 12;

export interface IChatWorkStatusMetadata {
  source: "work_status";
  workStatus: ChatWorkStatus;
}

/** Which app channel a "channel" conversation is. */
export interface IChatChannelConversationMetadata {
  channel: ChatChannel;
}

export type IChatConversationMetadata =
  | IChatWorkStatusMetadata
  | IChatChannelConversationMetadata;

// ─── Channels ──────────────────────────────────────────────────────────────

/** API ChatChannel. product_updates: the official MaiHRMS account. */
export type ChatChannel = "time_attendance" | "product_updates";

/** Channels made before `metadata.channel` existed are Time & Attendance. */
export const DEFAULT_CHAT_CHANNEL: ChatChannel = "time_attendance";

/** API ChatNoticeKind — data, never translated. In the channel settings' order. */
export const CHAT_NOTICE_KINDS = [
  "weekly_hours",
  "checkin_reminder",
  "late_checkin_reminder",
  "checkout_reminder",
  "late_checkout_reminder",
  "auto_clock_out",
  "admin_clocked_in",
  "admin_clocked_out",
  "payslip_ready",
  "salary_paid",
  "feature_update",
] as const;
export type ChatNoticeKind = (typeof CHAT_NOTICE_KINDS)[number];

/** Which kinds each channel sends (its settings list these). */
export const CHANNEL_NOTICE_KINDS: Record<ChatChannel, readonly ChatNoticeKind[]> = {
  time_attendance: CHAT_NOTICE_KINDS.filter((k) => k !== "feature_update"),
  product_updates: ["feature_update"],
};

/** One day of a weekly hours report (the person's local calendar day). */
export interface IChatNoticeDay {
  date: string;
  workedMinutes: number;
  overtimeMinutes: number;
}

/**
 * A channel's notice (metadata.notice): the client phrases it from the kind
 * and data. Data per kind:
 * - weekly_hours: { weekStart, weekEnd, days, totalMinutes, regularMinutes, overtimeMinutes }
 * - *_reminder: { minutes }
 * - auto_clock_out: { attendanceId, date }
 * - admin_clocked_in / admin_clocked_out: { attendanceId, adminId, adminName? }
 * - payslip_ready / salary_paid: { payrollRunId, period, currency, netPay }
 * - feature_update: { updateId, title, body (Markdown), mediaUrl, mediaKind: "image" | "video" | null }
 */
export interface IChatNoticeMetadata {
  channel: ChatChannel;
  kind: ChatNoticeKind | (string & {});
  data: Record<string, unknown>;
}

/** A tag kept by the server: `name` is the exact text after "@" in the body. */
export interface IChatMention {
  userId: number;
  name: string;
}

// ─── Shortcuts ─────────────────────────────────────────────────────────────

/** API ChatShortcutEntity — data, never translated. */
export const CHAT_SHORTCUT_ENTITY_TYPES = [
  "leave_request",
  "document_request",
  "expense",
  "asset_request",
  "event",
  "announcement",
  "task",
] as const;
export type ChatShortcutEntityType = (typeof CHAT_SHORTCUT_ENTITY_TYPES)[number];

/** API ChatShortcutAction — a Shortcut that opens a form (or the clock button). */
export const CHAT_SHORTCUT_ACTIONS = [
  "apply_leave",
  "request_document",
  "submit_expense",
  "request_asset",
  "clock_in",
] as const;
export type ChatShortcutAction = (typeof CHAT_SHORTCUT_ACTIONS)[number];

/**
 * GET /chat/shortcut-items — one of my shareable items, and the snapshot kept
 * on the message (title/status as they were when sent). Dates are the API's
 * text: `yyyy-MM-dd` or a Postgres timestamp.
 */
export interface IChatShortcutItem {
  type: ChatShortcutEntityType;
  /** For an event: the SENDER's invitation id. */
  id: number;
  /** Events: the event itself (what others open). */
  eventId: number | null;
  /** Tasks: the project whose board it's on. */
  projectId?: number | null;
  title: string;
  reference: string | null;
  startDate: string | null;
  endDate: string | null;
  amount: number | null;
  currency: string | null;
  status: string | null;
}

export interface IChatShortcutMetadata {
  /** A module (the original shortcut). */
  route?: string;
  params?: Record<string, string>;
  label?: string;
  /** A specific item; `ownerId` = the sender, whose item it is. */
  entity?: IChatShortcutItem & { ownerId: number };
  action?: ChatShortcutAction;
}

export type IChatOutgoingShortcut =
  | { route: string; label?: string }
  | { entity: { type: ChatShortcutEntityType; id: number } }
  | { action: ChatShortcutAction };

export interface IChatMessageMetadata {
  /** Groups/broadcasts: members tagged as "@Full Name" in the body. */
  mentions?: IChatMention[];
  location?: {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
  };
  contact?: {
    userId?: number;
    name: string;
    phoneNumber?: string;
    email?: string;
    profileImage?: string | null;
  };
  gif?: { url: string; previewUrl?: string; width?: number; height?: number };
  link?: { url: string; title?: string };
  shortcut?: IChatShortcutMetadata;
  notice?: IChatNoticeMetadata;
  poll?: { allowMultiple: boolean };
  /** A forwarded copy: forwards so far ("Forwarded many times" from 5). */
  forwarded?: { count: number };
  system?: {
    event: string;
    actorId?: number;
    targetUserIds?: number[];
    name?: string;
    /** message_pinned: the message that was pinned. */
    messageId?: number;
    /** Names of actorId / targetUserIds when it happened (id → full name). */
    names?: Record<string, string>;
  };
  /** waveform: loudness bars 0–100 sampled while recording (newer notes). */
  voice?: { durationMs?: number; waveform?: number[] };
}

export interface IChatUserSummary {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
}

export interface IChatContact extends IChatUserSummary {
  email: string | null;
  position: string | null;
  department: string | null;
}

export interface IChatAttachment {
  id?: number;
  fileId: string;
  fileUrl: string;
  fileName: string;
  mimeType: string;
  size: number;
  kind: ChatAttachmentKind;
  durationMs?: number | null;
}

export interface IChatPollOption {
  id: number;
  text: string;
  voteCount: number;
  voterIds: number[];
}

export interface IChatPoll {
  question: string;
  allowMultiple: boolean;
  totalVoters: number;
  options: IChatPollOption[];
}

export interface IChatReplyPreview {
  id: number;
  type: ChatMessageType;
  body: string | null;
  senderId: number | null;
  senderName: string | null;
  isDeleted: boolean;
}

/** Everyone who reacted with one emoji, first reaction first. */
export interface IChatReaction {
  emoji: string;
  count: number;
  userIds: number[];
}

export interface IChatMessage {
  id: number;
  conversationId: number;
  type: ChatMessageType;
  body: string | null;
  metadata: IChatMessageMetadata | null;
  sender: IChatUserSummary | null;
  replyTo: IChatReplyPreview | null;
  attachments: IChatAttachment[];
  poll: IChatPoll | null;
  /**
   * Grouped by emoji, the most used first; [] once deleted. One per person:
   * reacting again replaces mine.
   */
  reactions: IChatReaction[];
  /**
   * Voice notes only: who played it (never the sender); [] for anything else.
   * Grows live from `message_played`.
   */
  playedBy: number[];
  /** Pinned for everyone in the chat; null otherwise. */
  pinnedAt: string | null;
  pinnedBy: number | null;
  clientMessageId: string | null;
  isDeleted: boolean;
  editedAt: string | null;
  createdAt: string;
}

/** A message on screen: the server copy, or our optimistic one (negative id). */
export interface IChatLocalMessage extends IChatMessage {
  sendState?: "sending" | "failed";
}

export interface IChatLastMessage {
  id: number;
  type: ChatMessageType;
  body: string | null;
  metadata: IChatMessageMetadata | null;
  senderId: number | null;
  senderName: string | null;
  attachmentCount: number;
  isDeleted: boolean;
  createdAt: string;
}

export interface IChatPeer extends IChatUserSummary {
  workStatus: ChatWorkStatus;
  isActive: boolean;
  phoneNumber: string | null;
}

export interface IChatConversation {
  id: number;
  type: ChatConversationType;
  name: string | null;
  imageUrl: string | null;
  metadata: IChatConversationMetadata | null;
  /**
   * "Message yourself": a direct chat whose only member is me. `peer` is then
   * my own summary and `memberCount` 1 — no pushes, receipts, presence or typing.
   */
  isSelf: boolean;
  peer: IChatUserSummary | null;
  memberCount: number;
  myRole: ChatMemberRole;
  unreadCount: number;
  /** Unread messages that tag me. */
  unreadMentionCount: number;
  isArchived: boolean;
  /** Pinned to the top of my list (at most 3, the latest pin first). */
  isPinned: boolean;
  isMuted: boolean;
  mutedUntil: string | null;
  canSend: boolean;
  lastMessage: IChatLastMessage | null;
  lastActivityAt: string;
}

export interface IChatMember extends IChatUserSummary {
  role: ChatMemberRole;
  lastReadMessageId: number | null;
  /**
   * Everything up to this reached one of their devices. Read implies
   * delivered: use `deliveredUpTo()`, never this alone.
   */
  lastDeliveredMessageId: number | null;
  joinedAt: string;
}

export interface IChatConversationDetail extends Omit<IChatConversation, "peer"> {
  description: string | null;
  createdBy: number | null;
  peer: IChatPeer | null;
  members: IChatMember[];
  myLastReadMessageId: number | null;
  /**
   * My unread messages that tag me, oldest first (max 50), as of this
   * request's read pointer — a refetch after reading returns [].
   */
  unreadMentionIds: number[];
  /** The pinned bar: at most 3, the latest pin first. */
  pinnedMessages: IChatMessage[];
  /** This chat's own background; null = my default. */
  wallpaper: ChatWallpaper | null;
  /** What to draw. */
  effectiveWallpaper: ChatWallpaper;
}

export interface IChatSettings {
  wallpaper: ChatWallpaper;
  /** Channel notices I answered about: kind → keep / stop. Absent: on, not asked. */
  channelNotices?: Partial<Record<ChatNoticeKind, boolean>>;
}

export interface IChatPageMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

export interface IChatListResponse<T> {
  data: T[];
  meta: IChatPageMeta;
}

export interface IChatMessagePage {
  data: IChatMessage[];
  /**
   * `before` / first page: hasMore = older exist. `after`: hasMore = newer
   * exist. `around`: hasMore = older exist and hasNewer = newer exist.
   */
  meta: { hasMore: boolean; nextBefore: number | null; hasNewer?: boolean };
}

/** In-conversation search: newest first; `before=nextBefore` for the next page. */
export interface IChatMessageSearchPage {
  data: IChatMessage[];
  meta: { hasMore: boolean; nextBefore: number | null };
}

/**
 * GET /chat/search: matching messages from all my chats, and those chats
 * (once each) — both inside `data`, as the API's response wrapper keeps
 * only `data` and `meta`.
 */
export interface IChatGlobalSearchPage {
  data: { messages: IChatMessage[]; conversations: IChatConversation[] };
  meta: { hasMore: boolean; nextBefore: number | null };
}

export interface IChatUploadedFile {
  fileId: string;
  fileUrl: string;
  fileName: string;
  mimeType: string;
  size: number;
  kind: ChatAttachmentKind;
}

export type IChatMediaItem = IChatAttachment & {
  messageId: number;
  createdAt: string;
};

export interface IChatWorkStatusRecipients {
  workStatus: ChatWorkStatus;
  count: number;
  preview: IChatUserSummary[];
}

/** Who "Add by" can pull members from. `people` is the plain contact list. */
export const CHAT_AUDIENCE_KINDS = ["company", "branch", "department", "team", "project"] as const;
export type ChatAudienceKind = (typeof CHAT_AUDIENCE_KINDS)[number];

export interface IChatAudienceOption {
  id: number;
  name: string;
  /** Reachable colleagues in it, me excluded. */
  memberCount: number;
  /** Who they are: an option whose people are all selected is hidden. */
  memberIds: number[];
}

/**
 * `GET /chat/audiences` — already cut to what I may use. `company` is null and
 * an empty list means that kind is not offered.
 */
export interface IChatAudiences {
  company: { memberCount: number; memberIds: number[] } | null;
  branches: IChatAudienceOption[];
  departments: IChatAudienceOption[];
  teams: IChatAudienceOption[];
  projects: IChatAudienceOption[];
}

export interface IChatAudienceMembers {
  kind: ChatAudienceKind;
  count: number;
  members: IChatUserSummary[];
}

export interface IChatUnreadSummary {
  conversations: number;
  messages: number;
}

/** POST /chat/conversations/:id/messages — one payload field per type. */
export interface IChatOutgoingMessage {
  type: ChatMessageType;
  body?: string;
  replyToId?: number;
  attachments?: {
    fileId: string;
    fileName?: string;
    durationMs?: number;
    waveform?: number[];
  }[];
  location?: { latitude: number; longitude: number; name?: string };
  contact?: { userId?: number; name?: string; phoneNumber?: string; email?: string };
  link?: { url: string; title?: string };
  /** Exactly one of: a module, a specific item of mine, or a form to open. */
  shortcut?: IChatOutgoingShortcut;
  poll?: { question: string; options: string[]; allowMultiple?: boolean };
  /**
   * Groups/broadcasts: user ids tagged as "@Full Name" in `body`. The server
   * keeps a tag only while that text is there; the rest are dropped.
   */
  mentions?: number[];
  /** Groups/broadcasts: "@all" in `body` tags every member. Set by the API layer. */
  mentionAll?: boolean;
}

// ─── Scheduled messages ────────────────────────────────────────────────────

export type ChatScheduledStatus = "pending" | "sending" | "failed";

/** API limits (chat-message.dto.ts CHAT_SCHEDULE_*_LEAD_MS). */
export const CHAT_SCHEDULE_MIN_LEAD_MS = 60 * 1000;
export const CHAT_SCHEDULE_MAX_LEAD_MS = 365 * 24 * 60 * 60 * 1000;

/** GET /chat/conversations/:id/scheduled — only mine, soonest first. */
export interface IChatScheduledMessage {
  id: number;
  conversationId: number;
  type: ChatMessageType;
  body: string | null;
  /** The rest of the send request (replyToId, link, …). */
  payload: Record<string, unknown>;
  attachments: (IChatUploadedFile & { durationMs: number | null })[];
  sendAt: string;
  status: ChatScheduledStatus;
  failureCode: string | null;
  /** Already translated by the API. */
  failureMessage: string | null;
  createdAt: string;
}

/** POST /chat/conversations/:id/scheduled — the send body plus `sendAt`. */
export type IChatScheduleRequest = IChatOutgoingMessage & { sendAt: string };

// ─── Socket payloads (namespace /chat) ─────────────────────────────────────

export interface IChatReadReceipt {
  conversationId: number;
  userId: number;
  lastReadMessageId: number;
}

/** `message_delivered`: [userId]'s devices have everything up to the id. */
export interface IChatDeliveryReceipt {
  conversationId: number;
  userId: number;
  lastDeliveredMessageId: number;
}

/** `message_played`: [userId] played voice note [messageId] (never its sender). */
export interface IChatPlayReceipt {
  conversationId: number;
  messageId: number;
  userId: number;
}

export type ChatTypingActivity = "typing" | "recording";

export interface IChatTypingEvent {
  conversationId: number;
  userId: number;
  /** Their first name (the list knows no members to look it up in). */
  name?: string | null;
  isTyping: boolean;
  /** Typing text or recording a voice note (older servers: absent = typing). */
  activity?: ChatTypingActivity;
}

export interface IChatPresence {
  userId: number;
  online: boolean;
  lastSeenAt: string | null;
}

export interface IChatConversationRef {
  conversationId: number;
}
